import * as React from 'react';
import { Box } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import toast from 'react-hot-toast';

import { FormikTextField } from '@components/fieldBindings/FormikTextField';
import { FormikSubmitButton } from '@components/fieldBindings/FormikSubmitButton';
import { Link } from '@components/Link/Link';
import { RouteNames } from '@constants/RouteNames';
import client from '@api/client';
import i18n from '@src/i18n';
import { CenterColumnPage } from '../../Layouts/CenterColumnPage/CenterColumnPage';

type SignInFormValues = {
  email: string;
  password: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string().trim().email(i18n.t('modals.signUp.email.invalid')).required(i18n.t('common.required')),
  password: Yup.string().required(i18n.t('common.required')),
});

export const SignIn: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get('redirect');

  const handleSubmit = async ({ email, password }: SignInFormValues, util: FormikHelpers<SignInFormValues>) => {
    try {
      await client.login(email, password);
      history.push(redirect || RouteNames.ROOT);
    } catch (err) {
      toast.error(t('pages.signIn.errorMsg') as string);
      util.setSubmitting(false);
    }
  };

  return (
    <CenterColumnPage pageTitle={t('pages.signIn.titleText') as string}>
      <Formik<SignInFormValues>
        onSubmit={handleSubmit}
        initialValues={{ email: '', password: '' }}
        validateOnBlur={false}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty }) => (
          <Form>
            <Box display="flex" flexDirection="column" mb={2}>
              <FormikTextField
                id="email"
                name="email"
                type="email"
                placeholder={t('pages.signIn.email.placeholder')}
                margin="normal"
                autoComplete="email"
                autoFocus
              />
              <FormikTextField
                id="password"
                name="password"
                type="password"
                placeholder={t('pages.signIn.password.placeholder')}
                margin="normal"
                autoComplete="current-password"
              />
            </Box>
            <FormikSubmitButton disabled={!(isValid && dirty)}>{t('pages.signIn.submitButtonText')}</FormikSubmitButton>
          </Form>
        )}
      </Formik>
      <Box mt={2} textAlign="center">
        <Link to={RouteNames.ADMIN_LOGIN}>{t('pages.signIn.adminLoginLink')}</Link>
      </Box>
    </CenterColumnPage>
  );
};

export default SignIn;
